// "Agregar plataforma" (7.6F.2): the customer's own platform, for THIS
// workspace only. Four simple fields — no macros, JavaScript/iframe/SSL
// policies or weight limits (that is InteractAds' later verification). It
// starts UNVERIFIED and never becomes a global platform by itself. Obvious
// duplicates ("Stack Adapt" vs StackAdapt) are caught by the server, and the
// user is guided to the existing platform instead of creating another.
import { useQueryClient } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";
import { api, call } from "@/api/client";
import { ApiProblem } from "@/api/errors";
import { keys } from "@/api/hooks";
import type { CreativeProduct, PlatformSearchItem, PlatformType } from "@/api/types";
import { PLATFORM_TYPE_LABELS } from "@/lib/labels";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/ui/dialog";
import { Field } from "@/ui/field";
import { Input, NativeSelect, Textarea } from "@/ui/input";
import { Callout } from "@/ui/layout";
import type { SelectedPlatform } from "./platform-picker";

const TYPES = Object.keys(PLATFORM_TYPE_LABELS) as PlatformType[];

/** Mounted fresh on every opening (the caller keys it), prefilled with what the user searched. */
export function AddPlatformDialog({
  orgId,
  product,
  open,
  initialName,
  onOpenChange,
  onChosen,
}: {
  orgId: string;
  product: CreativeProduct;
  open: boolean;
  initialName: string;
  onOpenChange: (open: boolean) => void;
  onChosen: (choice: SelectedPlatform) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(initialName);
  const [platformType, setPlatformType] = useState<PlatformType | "">("");
  const [website, setWebsite] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [matches, setMatches] = useState<PlatformSearchItem[]>([]);
  const [pending, setPending] = useState(false);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const fieldErrors: Record<string, string> = {};
    if (name.trim() === "") fieldErrors.name = "Escribe el nombre de la plataforma.";
    if (platformType === "") fieldErrors.platformType = "Elige el tipo de plataforma.";
    if (website.trim() !== "" && !/^https?:\/\/\S+$/.test(website.trim()))
      fieldErrors.websiteUrl = "Escribe la dirección completa, por ejemplo https://ejemplo.com.";
    setErrors(fieldErrors);
    setMatches([]);
    if (Object.keys(fieldErrors).length > 0 || platformType === "") return;
    setPending(true);
    try {
      const created = await call(() =>
        api.POST("/orgs/{orgId}/custom-platforms", {
          params: { path: { orgId } },
          body: {
            name: name.trim(),
            platformType,
            ...(website.trim() ? { websiteUrl: website.trim() } : {}),
            ...(note.trim() ? { note: note.trim() } : {}),
          },
        })
      );
      await queryClient.invalidateQueries({ queryKey: [...keys.org(orgId), "platform-search"] });
      onChosen({ kind: "custom", id: created.id, name: created.name, platformType: created.platformType });
    } catch (problem) {
      if (problem instanceof ApiProblem && problem.code === "PLATFORM_EXISTS") {
        // Search again (names, aliases, own platforms) and offer the existing one.
        const found = await call(() =>
          api.GET("/orgs/{orgId}/advertising-platforms", {
            params: { path: { orgId }, query: { q: name.trim(), product } },
          })
        ).catch(() => ({ items: [] as PlatformSearchItem[] }));
        setMatches(found.items.slice(0, 3));
        setErrors({ form: problem.message });
      } else {
        setErrors(
          problem instanceof ApiProblem
            ? { ...problem.fieldErrors(), form: problem.message }
            : { form: "No se pudo agregar la plataforma." }
        );
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Agregar plataforma"
        description="Se agrega solo a tu espacio de trabajo. InteractAds aún no habrá verificado su compatibilidad."
      >
        <form className="grid gap-4" onSubmit={(e) => void submit(e)} noValidate>
          <Field label="Nombre de la plataforma" error={errors.name}>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              maxLength={120}
              autoFocus
              required
            />
          </Field>
          <Field label="Tipo de plataforma" error={errors.platformType}>
            <NativeSelect
              value={platformType}
              onChange={(e) => {
                setPlatformType(e.target.value as PlatformType | "");
              }}
              required
            >
              <option value="">Selecciona…</option>
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {PLATFORM_TYPE_LABELS[t]}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Sitio web (opcional)" error={errors.websiteUrl}>
            <Input
              value={website}
              onChange={(e) => {
                setWebsite(e.target.value);
              }}
              inputMode="url"
              placeholder="https://"
              maxLength={2048}
            />
          </Field>
          <Field label="Nota (opcional)" error={errors.note} hint="Por ejemplo, quién la usa en tu equipo.">
            <Textarea
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
              maxLength={500}
              rows={2}
            />
          </Field>
          {errors.form ? (
            <Callout tone="warning" title="Ya existe">
              <span role="alert">{errors.form}</span>
              {matches.length > 0 ? (
                <ul className="mt-2 grid gap-1.5">
                  {matches.map((m) => (
                    <li key={`${m.kind}-${m.id}`} className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{m.name}</span>
                      <span className="text-xs text-muted">
                        {PLATFORM_TYPE_LABELS[m.platformType]}
                        {m.kind === "custom" ? " · personalizada" : ""}
                      </span>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          onChosen({ kind: m.kind, id: m.id, name: m.name, platformType: m.platformType });
                        }}
                      >
                        Usar esta plataforma
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </Callout>
          ) : null}
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={pending}>
              Agregar plataforma
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
